import { FileText, Users } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { mxn } from "@/lib/format";
import { GRADO_TONE } from "@/lib/riesgoTokens";
import { ROL_LABEL, sumaMontos } from "@/lib/integrantesGrupo";
import { DatosGenerales, Expediente } from "@/components/solicitudes/comite/IntegranteExpediente";
import { EstatusPld, PerfilTransaccional } from "@/components/solicitudes/comite/IntegrantePerfilPld";
import { ConsultaSic } from "@/components/solicitudes/comite/IntegranteSic";

const ROLES = { ...ROL_LABEL, solicitante: "Solicitante" };

function Encabezado({ f }) {
  return (
    <span className="flex flex-1 flex-wrap items-center gap-2 text-left">
      <span className="font-medium text-slate-800">{f.cliente}</span>
      <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${f.rol === "obligado_solidario" ? "bg-slate-200 text-slate-700" : "bg-slate-800 text-white"}`}>{ROLES[f.rol] || f.rol}</span>
      <span className={`rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${GRADO_TONE[f.pld?.grado_riesgo] || "border-slate-200 bg-slate-100 text-slate-600"}`}>Riesgo {f.pld?.grado_riesgo || "sin evaluar"}</span>
      <span className="text-[11px] text-slate-500">Expediente {f.expediente?.estatus}</span>
      <span className="ml-auto mr-2 font-semibold text-slate-700">{mxn(f.monto)}</span>
    </span>
  );
}

/** Comité · expediente del solicitante (simple) o de cada integrante (grupal). Solo lectura. */
export function ExpedienteComite({ expediente }) {
  const fichas = expediente?.fichas || [];
  if (!fichas.length) return null;
  const grupal = expediente.tipo === "grupal";
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="comite-expediente">
      <div className="mb-1 flex flex-wrap items-center gap-2">
        {grupal ? <Users className="h-4 w-4 text-primary" /> : <FileText className="h-4 w-4 text-primary" />}
        <p className="font-heading text-sm font-semibold text-slate-800" data-testid="comite-expediente-titulo">
          {grupal ? `Integrantes del grupo (${fichas.length})` : "Expediente del solicitante"}
        </p>
        <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">Solo lectura</span>
        {grupal && <span className="ml-auto text-xs font-semibold text-slate-700" data-testid="comite-integrantes-suma">Suma solicitada: {mxn(sumaMontos(fichas))}</span>}
      </div>
      {grupal && (
        <p className="mb-2 text-[11px] text-slate-500">
          Si autorizas un monto menor a la suma, al contratar se ajustan los montos de los integrantes hasta igualarlo; no se agregan ni se quitan integrantes.
        </p>
      )}
      <Accordion type="multiple" defaultValue={grupal ? [] : [fichas[0].cliente_id]} className="space-y-1.5">
        {fichas.map((f) => (
          <AccordionItem key={f.cliente_id} value={f.cliente_id} className="rounded-md border border-slate-200 px-2.5"
            data-testid={`comite-ficha-${f.cliente_id}`}>
            <AccordionTrigger className="py-2 text-[13px] hover:no-underline" data-testid={`comite-ficha-toggle-${f.cliente_id}`}>
              <Encabezado f={f} />
            </AccordionTrigger>
            <AccordionContent className="grid grid-cols-1 gap-2 pb-3 lg:grid-cols-2">
              <DatosGenerales f={f} />
              <EstatusPld f={f} />
              <Expediente f={f} />
              <PerfilTransaccional f={f} />
              <ConsultaSic f={f} />
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}

export default ExpedienteComite;
